import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Crown,
  Sparkles,
  Clock,
  CheckCircle2,
  XCircle,
  Banknote,
  ExternalLink,
  TrendingUp,
  Lock,
  Sticker,
  Store,
  Share2,
  BarChart3,
  Trophy,
  Gift,
} from "lucide-react";
import { api } from "@/lib/api";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useAuth } from "@/context/AuthContext";
import { MICROTX_ENABLED, X_HANDLE } from "@/config";
import { recordShare, localShares } from "@/lib/shareStats";
import { cn } from "@/lib/utils";
import { AuthImage } from "@/components/common/AuthImage";
import { ShareToXButton } from "@/components/common/ShareToX";
import { PaywallOverlay } from "@/components/common/PaywallOverlay";
import { FanTiersPanel } from "@/components/creator/FanTiersPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Flag-gated shell (REACT_APP_MICROTX). Shows the Circle-Pro take-rate incentive.
// Self-hides unless the backend surfaces `platform_fee_bps_pro` and the owner isn't already Pro.
function TakeRateBanner() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["creator-earnings"], queryFn: api.creatorEarnings, retry: false });
  if (!data || data.platform_fee_bps_pro == null || data.is_bloq_pro) return null;
  const feeBps = data.platform_fee_bps ?? 1500;
  const keepNow = 100 - feeBps / 100;
  const keepPro = 100 - data.platform_fee_bps_pro / 100;
  if (keepPro <= keepNow) return null;
  return (
    <div className="flex items-center gap-3 rounded-3xl border border-bloq-purple/30 bg-bloq-purple/[0.07] p-5" data-testid="take-rate-banner">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-bloq-purple/15 text-bloq-purple"><TrendingUp className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white">
          You keep <span className="text-bloq-mint">{keepNow}%</span> today → <span className="text-bloq-purple">{keepPro}% with Circle Pro</span>
        </div>
        <div className="text-xs text-bloq-muted">Lower the platform take-rate on every membership, tip, boost & sticker sale.</div>
      </div>
      <Button onClick={() => navigate("/app/membership")} className="shrink-0 rounded-full bg-bloq-purple font-bold text-white hover:bg-bloq-purple/90" data-testid="take-rate-upgrade-btn">
        <Crown className="mr-1.5 h-4 w-4" /> Upgrade
      </Button>
    </div>
  );
}

function SectionCard({ children, className, testid }) {
  return (
    <div className={cn("rounded-3xl border border-white/8 bg-bloq-s1 p-6", className)} data-testid={testid}>
      {children}
    </div>
  );
}

// Featured sticker packs strip — surfaces published store packs on the Creator Hub so creators can
// discover + promote them. Prefers the `featured` scope, falls back to `store`. Each card deep-links
// to the shareable pack page; the share icon copies ${SHARE_BASE}/app/stickers/{id}.
function FeaturedPacksStrip() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["creator-featured-packs"],
    queryFn: async () => {
      const f = await api.stickerPacks("featured").catch(() => null);
      if (f?.items?.length) return f.items;
      const s = await api.stickerPacks("store").catch(() => null);
      return s?.items || [];
    },
    retry: false,
  });
  const packs = data || [];
  if (packs.length === 0) return null;
  const share = async (e, packId) => {
    e.stopPropagation();
    const url = api.stickerShareUrl(packId);
    recordShare(packId);
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  return (
    <SectionCard testid="featured-packs-strip">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-white"><Store className="h-4 w-4 text-bloq-mint" /> Featured sticker packs</div>
        <span className="text-xs text-bloq-muted">Tap to preview · share to promote</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {packs.map((pack) => (
          <button
            key={pack.pack_id}
            onClick={() => navigate(`/app/stickers/${pack.pack_id}`)}
            className="group w-36 shrink-0 rounded-2xl border border-white/8 bg-bloq-s2 p-3 text-left transition-colors hover:border-bloq-mint/40"
            data-testid={`featured-pack-${pack.pack_id}`}
          >
            <div className="mb-2 flex h-20 w-full items-center justify-center overflow-hidden rounded-xl bg-bloq-bg">
              {pack.cover_url
                ? <AuthImage src={api.stickerCoverSrc(pack.cover_url)} alt="" className="h-full w-full object-contain" />
                : <Sticker className="h-8 w-8 text-bloq-muted" />}
            </div>
            <div className="truncate text-sm font-semibold text-white">{pack.title}</div>
            <div className="truncate text-[11px] text-bloq-muted">{pack.author?.handle ? `@${pack.author.handle}` : ""}{pack.sticker_count ? ` · ${pack.sticker_count}` : ""}</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[11px] font-bold text-bloq-mint">{pack.price ? usd(pack.price / 100) : "Free"}</span>
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => share(e, pack.pack_id)}
                className="rounded-full p-1 text-bloq-muted hover:text-white"
                title="Copy share link"
                data-testid={`featured-share-${pack.pack_id}`}
              >
                <Share2 className="h-3.5 w-3.5" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

function ApplyDialog({ open, onOpenChange, onApplied, onUpgradeNeeded }) {
  const [pitch, setPitch] = useState("");
  const [pricing, setPricing] = useState("");
  const [links, setLinks] = useState("");
  const [busy, setBusy] = useState(false);

  const ready = pitch.trim().length >= 20 && pricing.trim().length > 0;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await api.creatorApply({ pitch: pitch.trim(), planned_pricing: pricing.trim(), links: links.trim() || null });
      toast.success("Application submitted — we'll review it shortly");
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "upgrade_required" || e?.response?.status === 429) {
        toast.error(d?.message || "The creator program is a Circle Pro perk.");
        onOpenChange(false);
        onUpgradeNeeded?.();
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Couldn't submit application");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setPitch(""); setPricing(""); setLinks(""); } }}>
      <DialogContent className="max-w-lg border-white/10 bg-bloq-s1 text-white" data-testid="creator-apply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Sparkles className="h-4 w-4 text-bloq-purple" /> Apply to the Creator Program
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Tell us how you'll monetize. Approved creators can charge for premium BLOQs and get paid out via Stripe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Your pitch</Label>
            <Textarea
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              placeholder="What premium value will you offer your community? (min 20 chars)"
              className="min-h-24 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="creator-pitch-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Planned pricing</Label>
            <Input
              value={pricing}
              onChange={(e) => setPricing(e.target.value)}
              placeholder="e.g. $9.99 / month per member"
              className="h-11 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="creator-pricing-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Links (optional)</Label>
            <Input
              value={links}
              onChange={(e) => setLinks(e.target.value)}
              placeholder="Twitter / website / portfolio"
              className="h-11 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="creator-links-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!ready || busy}
            className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-60"
            data-testid="creator-apply-submit"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EarningsPanel() {
  const { data, isLoading } = useQuery({ queryKey: ["creator-earnings"], queryFn: api.creatorEarnings });
  if (isLoading) {
    return (
      <SectionCard testid="creator-earnings-card">
        <div className="flex justify-center py-6 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </SectionCard>
    );
  }
  const payments = data?.payments || [];
  return (
    <SectionCard testid="creator-earnings-card">
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
        <TrendingUp className="h-4 w-4 text-bloq-mint" /> Earnings
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-white/8 bg-bloq-s2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Net</div>
          <div className="text-lg font-bold text-bloq-mint" data-testid="creator-net-usd">{usd(data?.net_usd)}</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-bloq-s2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Gross</div>
          <div className="text-lg font-bold">{usd(data?.gross_usd)}</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-bloq-s2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-bloq-muted">Fees ({data?.platform_fee_pct ?? 15}%)</div>
          <div className="text-lg font-bold text-bloq-muted">{usd(data?.fees_usd)}</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-bloq-muted">
          Recent payouts ({data?.count ?? payments.length})
        </div>
        {payments.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-white/10 py-6 text-center text-sm text-bloq-muted" data-testid="creator-earnings-empty">
            No payments yet. Set a price on one of your BLOQs to start earning.
          </p>
        ) : (
          <div className="space-y-1.5">
            {payments.slice(0, 20).map((p, i) => (
              <div key={p.id || i} className="flex items-center justify-between rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2 text-sm" data-testid={`creator-earning-row-${i}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.circle_name || p.description || "Membership"}</div>
                  <div className="text-[11px] text-bloq-muted">{p.buyer_name || p.buyer || ""}{p.created_at ? ` · ${new Date(p.created_at).toLocaleDateString()}` : ""}</div>
                </div>
                <div className="shrink-0 font-bold text-bloq-mint">{usd(p.net_usd ?? p.amount_usd)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function PayoutPanel({ status, connectConfigured, onRefresh }) {
  const nativePurchases = usePublicConfig()?.native_purchases === true;
  const [busy, setBusy] = useState(false);
  const connect = status?.connect || {};
  const ready = connect.payouts_enabled || connect.charges_enabled;

  const onboard = async () => {
    setBusy(true);
    try {
      const res = await api.creatorConnectOnboard(`${window.location.origin}/app/creator?connect=return`);
      const url = res?.url || res?.onboarding_url;
      if (url) window.location.href = url;
      else { toast.error("Couldn't start payout setup"); setBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start payout setup");
      setBusy(false);
    }
  };

  return (
    <SectionCard testid="creator-payout-card">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Banknote className="h-4 w-4 text-bloq-mint" /> Payouts
      </div>
      {!connectConfigured ? (
        <p className="text-sm text-bloq-muted" data-testid="creator-connect-unconfigured">
          Payout setup isn't enabled in this environment yet. Once it's live you'll connect a Stripe account here to receive earnings.
        </p>
      ) : ready ? (
        <div className="flex items-center gap-2 text-sm text-bloq-mint" data-testid="creator-connect-status">
          <CheckCircle2 className="h-4 w-4" /> Payouts active — you're all set to receive earnings.
        </div>
      ) : nativePurchases ? (
        <p className="text-sm text-bloq-muted" data-testid="creator-connect-native">Manage payouts in the mobile app.</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-bloq-muted" data-testid="creator-connect-status">
            {connect.details_submitted ? "Your payout details are being verified by Stripe." : "Connect a Stripe account to receive your earnings."}
          </p>
          <Button
            onClick={onboard}
            disabled={busy}
            className="rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
            data-testid="creator-connect-onboard-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Banknote className="mr-1.5 h-4 w-4" /> Set up payouts with Stripe</>}
          </Button>
        </>
      )}
    </SectionCard>
  );
}

// Creator-facing share funnel per pack. Real cross-user Opens/Adds come from the mobile analytics
// endpoint (GET /creator/stickers/analytics); falls back to the creator's own local share taps.
function SharePerformance({ userId }) {
  const [, setTick] = useState(0);
  const { data: owned } = useQuery({ queryKey: ["creator-owned-packs"], queryFn: () => api.stickerPacks("owned"), retry: false });
  const { data: analytics } = useQuery({ queryKey: ["creator-sticker-analytics"], queryFn: api.creatorStickerAnalytics, retry: false });
  const myPacks = (owned?.items || []).filter((p) => p.author?.id === userId && p.status !== "removed");
  if (myPacks.length === 0) return null;
  const hasBackend = Array.isArray(analytics?.items);
  const byId = {};
  (analytics?.items || []).forEach((a) => { byId[a.pack_id] = a; });
  const copy = async (packId) => {
    const url = api.stickerShareUrl(packId);
    recordShare(packId);
    setTick((t) => t + 1);
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  return (
    <SectionCard testid="share-performance">
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><BarChart3 className="h-4 w-4 text-bloq-mint" /> Share performance</div>
      <p className="mt-0.5 text-xs text-bloq-muted">See which packs your shared links actually convert.</p>
      {!hasBackend && (
        <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-300" data-testid="share-perf-pending">
          Opens &amp; adds activate once share analytics ship — showing your shares from this device for now.
        </div>
      )}
      <div className="mt-3 space-y-2" data-testid="share-perf-list">
        <div className="grid grid-cols-[1fr_2.5rem_2.5rem_2.5rem_3rem] items-center gap-2 px-1 text-[10px] uppercase tracking-wider text-bloq-muted">
          <span>Pack</span>
          <span className="text-right">Shares</span>
          <span className="text-right">Opens</span>
          <span className="text-right">Adds</span>
          <span className="text-right">Conv.</span>
        </div>
        {myPacks.map((p) => {
          const a = byId[p.pack_id];
          const shares = hasBackend ? (a?.shares ?? 0) : localShares(p.pack_id);
          const opens = hasBackend ? (a?.opens ?? 0) : null;
          const adds = hasBackend ? (a?.adds ?? 0) : null;
          const conv = hasBackend && opens ? `${Math.round(((adds || 0) / opens) * 100)}%` : "—";
          return (
            <div key={p.pack_id} className="grid grid-cols-[1fr_2.5rem_2.5rem_2.5rem_3rem] items-center gap-2 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2.5" data-testid={`share-perf-${p.pack_id}`}>
              <div className="flex min-w-0 items-center gap-1">
                <button onClick={() => copy(p.pack_id)} className="shrink-0 rounded-full p-1.5 text-bloq-muted hover:text-white" title="Copy share link" data-testid={`share-perf-copy-${p.pack_id}`}>
                  <Share2 className="h-3.5 w-3.5" />
                </button>
                <ShareToXButton
                  iconOnly
                  text={`I just dropped ${p.title} on @${X_HANDLE} 🔥`}
                  url={api.stickerShareUrl(p.pack_id)}
                  onShare={() => { recordShare(p.pack_id); setTick((t) => t + 1); }}
                  testid={`share-perf-x-${p.pack_id}`}
                />
                <span className="truncate text-sm text-white">{p.title}</span>
              </div>
              <span className="text-right text-sm font-semibold text-white" data-testid={`share-perf-shares-${p.pack_id}`}>{shares}</span>
              <span className="text-right text-sm text-bloq-muted">{opens ?? "—"}</span>
              <span className="text-right text-sm text-bloq-muted">{adds ?? "—"}</span>
              <span className="text-right text-sm text-bloq-mint">{conv}</span>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// Weekly creator leaderboard by pack adds — friendly competition that nudges creators to publish + promote.
// Data from GET /creators/leaderboard?period=week (mobile-owned); shows a motivational empty state until live.
function CreatorLeaderboard({ userId }) {
  const { data } = useQuery({ queryKey: ["creator-leaderboard-week"], queryFn: api.creatorLeaderboard, retry: false });
  const items = (data?.items || []).slice(0, 5);
  const rankTint = (r) => (r === 1 ? "bg-amber-400/20 text-amber-300" : r === 2 ? "bg-slate-300/20 text-slate-200" : r === 3 ? "bg-orange-400/20 text-orange-300" : "bg-white/8 text-bloq-muted");
  const initials = (name) => ((name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?");
  return (
    <SectionCard testid="creator-leaderboard">
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><Trophy className="h-4 w-4 text-bloq-mint" /> Creator leaderboard</div>
      <p className="mt-0.5 text-xs text-bloq-muted">Top creators by pack adds this week.</p>
      {items.length === 0 ? (
        <div className="mt-3 rounded-xl border border-white/8 bg-bloq-s2 px-4 py-6 text-center text-xs text-bloq-muted" data-testid="leaderboard-empty">
          The board's still warming up — publish &amp; share packs to be the first creator to climb it this week.
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid="leaderboard-list">
          {items.map((c) => {
            const me = c.user_id && c.user_id === userId;
            return (
              <div
                key={c.rank}
                className={cn("flex items-center gap-3 rounded-xl border px-3 py-2.5", me ? "border-bloq-mint/40 bg-bloq-mint/[0.06]" : "border-white/8 bg-bloq-s2")}
                data-testid={`leaderboard-row-${c.rank}`}
              >
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold", rankTint(c.rank))}>{c.rank}</span>
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bloq-purple/20 text-[11px] font-bold text-bloq-purple">{initials(c.display_name || c.handle)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">
                    {c.display_name || `@${c.handle}`}
                    {me && <span className="ml-1.5 text-[10px] font-bold text-bloq-mint">you</span>}
                  </div>
                  {c.handle && <div className="truncate text-[11px] text-bloq-muted">@{c.handle}</div>}
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-bloq-mint">{c.adds ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-bloq-muted">adds</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// Tip leaderboard — "Top tippers this week". Flag-gated shell; empty state until the
// backend surfaces GET /tips/leaderboard (see HANDOFF_differentiation_v1.md).
function TipLeaderboard({ userId }) {
  const { data } = useQuery({ queryKey: ["tip-leaderboard-week"], queryFn: () => api.tipLeaderboard("week", "global"), retry: false });
  const items = (data?.top || data?.items || []).slice(0, 5);
  const rankTint = (r) => (r === 1 ? "bg-amber-400/20 text-amber-300" : r === 2 ? "bg-slate-300/20 text-slate-200" : r === 3 ? "bg-orange-400/20 text-orange-300" : "bg-white/8 text-bloq-muted");
  const initials = (name) => ((name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?");
  return (
    <SectionCard testid="tip-leaderboard">
      <div className="flex items-center gap-2 text-sm font-semibold text-white"><Gift className="h-4 w-4 text-bloq-purple" /> Top tippers this week</div>
      <p className="mt-0.5 text-xs text-bloq-muted">The community's most generous supporters.</p>
      {items.length === 0 ? (
        <div className="mt-3 rounded-xl border border-white/8 bg-bloq-s2 px-4 py-6 text-center text-xs text-bloq-muted" data-testid="tip-leaderboard-empty">
          No tips yet this week — send a tip in any chat to kick off the board. 🎉
        </div>
      ) : (
        <div className="mt-3 space-y-2" data-testid="tip-leaderboard-list">
          {items.map((row, idx) => {
            const u = row.user || row;
            const rank = row.rank ?? idx + 1;
            const me = u.id && u.id === userId;
            return (
              <div
                key={rank}
                className={cn("flex items-center gap-3 rounded-xl border px-3 py-2.5", me ? "border-bloq-mint/40 bg-bloq-mint/[0.06]" : "border-white/8 bg-bloq-s2")}
                data-testid={`tip-leaderboard-row-${rank}`}
              >
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold", rankTint(rank))}>{rank}</span>
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bloq-purple/20 text-[11px] font-bold text-bloq-purple">{initials(u.display_name || u.handle)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">
                    {u.display_name || `@${u.handle}`}
                    {me && <span className="ml-1.5 text-[10px] font-bold text-bloq-mint">you</span>}
                  </div>
                  {u.handle && <div className="truncate text-[11px] text-bloq-muted">@{u.handle}</div>}
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-bloq-mint">{usd((row.amount || 0) / 100)}</div>
                  <div className="text-[10px] uppercase tracking-wide text-bloq-muted">{row.count ?? 0} tips</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// Roadmap teaser that builds anticipation for approved creators — purely informational, no CTA.
const COMING_SOON = [
  { icon: Gift, title: "In-chat crypto tips", desc: "Send stablecoins to anyone, right inside the chat." },
  { icon: Sticker, title: "Animated sticker packs", desc: "Publish premium animated packs to the store." },
  { icon: BarChart3, title: "Deeper payout insights", desc: "Revenue trends, churn & retention analytics." },
];

function WhatsComingCard() {
  return (
    <SectionCard className="border-bloq-mint/20 bg-bloq-mint/[0.04]" testid="whats-coming-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-bloq-mint" /> What's coming for creators</div>
        <span className="rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-mint">Soon</span>
      </div>
      <p className="mt-0.5 text-xs text-bloq-muted">A peek at what we're building to help you earn more.</p>
      <div className="mt-3 space-y-2">
        {COMING_SOON.map((f) => (
          <div key={f.title} className="flex items-start gap-3 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2.5" data-testid={`coming-soon-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-bloq-mint/15 text-bloq-mint"><f.icon className="h-4 w-4" /></span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white">{f.title}</div>
              <div className="text-xs text-bloq-muted">{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export default function CreatorPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applyOpen, setApplyOpen] = useState(false);
  const { data: status, isLoading } = useQuery({ queryKey: ["creator-status"], queryFn: api.creatorStatus });

  // Return from Stripe Connect onboarding.
  useEffect(() => {
    if (searchParams.get("connect") === "return") {
      qc.invalidateQueries({ queryKey: ["creator-status"] });
      toast.success("Payout setup updated");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchStatus = () => qc.invalidateQueries({ queryKey: ["creator-status"] });
  const goUpgrade = () => navigate("/app/membership");

  const cs = status?.creator_status || "none";
  const app = status?.application;

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="creator-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/app/profile")} className="rounded-full p-1 text-bloq-muted hover:text-white" data-testid="creator-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Crown className="h-5 w-5 text-bloq-purple" /> Creator Hub
            </h1>
            <p className="mt-0.5 text-sm text-bloq-muted">Monetize your BLOQs — keep {100 - (status?.platform_fee_pct ?? 15)}% of every membership.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="mt-6 space-y-4">
            {MICROTX_ENABLED && <TakeRateBanner />}
            {MICROTX_ENABLED && <FeaturedPacksStrip />}
            {/* NOT A CREATOR */}
            {cs === "none" && (() => {
              const introCard = (
                <SectionCard testid="creator-intro-card">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bloq-purple/15 text-bloq-purple"><Sparkles className="h-6 w-6" /></span>
                    <div>
                      <div className="text-lg font-bold">Turn your community into income</div>
                      <div className="text-sm text-bloq-muted">Charge a monthly membership for premium BLOQs.</div>
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm text-white/80">
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bloq-mint" /> Set your own price per Circle</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bloq-mint" /> Get paid out securely via Stripe</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bloq-mint" /> Owner analytics on members & revenue</li>
                  </ul>
                </SectionCard>
              );
              return status?.can_apply ? (
                <>
                  {introCard}
                  <Button
                    onClick={() => setApplyOpen(true)}
                    className="w-full rounded-full bloq-gradient py-6 text-base font-bold text-bloq-bg hover:opacity-90"
                    data-testid="creator-apply-btn"
                  >
                    <Sparkles className="mr-1.5 h-5 w-5" /> Apply to become a creator
                  </Button>
                </>
              ) : (
                <PaywallOverlay
                  title="Creator Program"
                  description="A Circle Pro perk — monetize your BLOQs with paid memberships, owner analytics & Stripe payouts."
                  ctaLabel="Upgrade to Circle Pro"
                  icon={Crown}
                  testid="creator-upgrade-cta"
                >
                  {introCard}
                </PaywallOverlay>
              );
            })()}

            {/* PENDING */}
            {cs === "pending" && (
              <SectionCard testid="creator-status-pending">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-400"><Clock className="h-6 w-6" /></span>
                  <div>
                    <div className="text-lg font-bold">Application under review</div>
                    <div className="text-sm text-bloq-muted">We'll notify you once it's approved.</div>
                  </div>
                </div>
                {app && (
                  <div className="mt-4 space-y-2 rounded-2xl border border-white/8 bg-bloq-s2 p-4 text-sm">
                    <div><span className="text-bloq-muted">Pitch: </span>{app.pitch}</div>
                    <div><span className="text-bloq-muted">Pricing: </span>{app.planned_pricing}</div>
                    {app.created_at && <div className="text-[11px] text-bloq-muted">Submitted {new Date(app.created_at).toLocaleDateString()}</div>}
                  </div>
                )}
              </SectionCard>
            )}

            {/* REJECTED */}
            {cs === "rejected" && (
              <SectionCard className="border-destructive/25" testid="creator-status-rejected">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/15 text-destructive"><XCircle className="h-6 w-6" /></span>
                  <div>
                    <div className="text-lg font-bold">Application not approved</div>
                    <div className="text-sm text-bloq-muted">{app?.review_note || app?.reason || "You can refine your pitch and apply again."}</div>
                  </div>
                </div>
                {status?.can_apply && (
                  <Button onClick={() => setApplyOpen(true)} className="mt-4 rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="creator-reapply-btn">
                    Apply again
                  </Button>
                )}
              </SectionCard>
            )}

            {/* APPROVED */}
            {cs === "approved" && (
              <>
                <SectionCard className="border-bloq-mint/30 bg-bloq-mint/[0.05]" testid="creator-status-approved">
                  <div className="flex items-center gap-2 text-sm font-semibold text-bloq-mint">
                    <CheckCircle2 className="h-4 w-4" /> You're an approved creator
                  </div>
                  <p className="mt-1 text-sm text-bloq-muted">
                    Open any BLOQ you own → Info → Monetization to set a membership price. You keep {100 - (status?.platform_fee_pct ?? 15)}% of each payment.
                  </p>
                </SectionCard>
                <PayoutPanel status={status} connectConfigured={status?.connect_configured} onRefresh={refetchStatus} />
                <FanTiersPanel userId={user?.id} connectReady={status?.connect_configured} />
                <EarningsPanel />
                {MICROTX_ENABLED && (
                  <SectionCard className="border-bloq-purple/30 bg-bloq-purple/[0.05]" testid="creator-sticker-studio-card">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          <Sticker className="h-4 w-4 text-bloq-purple" /> Sticker Studio
                        </div>
                        <p className="mt-1 text-sm text-bloq-muted">Design sticker packs and publish them to the store — earn a revenue share on every sale.</p>
                      </div>
                      <Button onClick={() => navigate("/app/creator/stickers")} className="shrink-0 rounded-full bg-bloq-purple font-bold text-white hover:bg-bloq-purple/90" data-testid="open-sticker-studio-btn">
                        Open
                      </Button>
                    </div>
                  </SectionCard>
                )}
                {MICROTX_ENABLED && <SharePerformance userId={user?.id} />}
                {MICROTX_ENABLED && <CreatorLeaderboard userId={user?.id} />}
                {MICROTX_ENABLED && <TipLeaderboard userId={user?.id} />}
                {MICROTX_ENABLED && <WhatsComingCard />}
              </>
            )}

            <p className="pt-1 text-center text-xs text-bloq-muted">
              <Lock className="mr-1 inline h-3 w-3" /> Payments processed securely by Stripe. Platform fee: {status?.platform_fee_pct ?? 15}%.
            </p>
          </div>
        )}
      </div>

      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} onApplied={refetchStatus} onUpgradeNeeded={goUpgrade} />
    </div>
  );
}
